<template>
  <div
    class="options-ui-container"
    ref="optionsUiContainerElement"
  >
    <div class="options-ui-view-buttons">
      <button
        type="button"
        @click="setOptionsModeView(OPTIONS_MODE_STRADDLE_VIEW)"
        class="options-ui-view-button"
        :class="{ 'options-ui-view-button-active': optionsModeView === OPTIONS_MODE_STRADDLE_VIEW }"
      >
        <i class="fas fa-fw fa-game-board-alt"></i>
        &nbsp;&nbsp;Straddle View
      </button>
      <button
        type="button"
        @click="setOptionsModeView(OPTIONS_MODE_LIST_VIEW)"
        class="options-ui-view-button"
        :class="{ 'options-ui-view-button-active': optionsModeView === OPTIONS_MODE_LIST_VIEW }"
      >
        <i class="far fa-fw fa-table"></i>
        &nbsp;&nbsp;List View
      </button>
      <button
        type="button"
        @click="setOptionsModeView(OPTIONS_MODE_OPTIONS_PLAY_VIEW)"
        class="options-ui-view-button"
        :class="{ 'options-ui-view-button-active': optionsModeView === OPTIONS_MODE_OPTIONS_PLAY_VIEW }"
      >
        <img
          src="//d.stockcharts.com/img/optionsplay-sm.png"
          alt="OptionsPlay"
        />
      </button>
    </div>
    <div class="options-ui-header" v-show="optionsModeView === OPTIONS_MODE_STRADDLE_VIEW || optionsModeView === OPTIONS_MODE_LIST_VIEW">
      <h3 class="symbol-heading">{{ formattedSymbol }}</h3>
      <span v-show="nextEarningsDate">Next Earnings Date: {{ nextEarningsDate }}</span>
      <span v-show="lastEarningsDate">Last Earnings Date: {{ lastEarningsDate }}</span>
    </div>
    <div class="table-ui-container" v-show="(optionsModeView === OPTIONS_MODE_STRADDLE_VIEW || optionsModeView === OPTIONS_MODE_LIST_VIEW) && !isLoading && expirations.length > 0">
      <div class="options-select-container">
        <label class="sc3-form-label sc3-form-label-inline sc3-form-label-small" for="options-view-expiration">EXPIRATION</label>
        <br />
        <select
          id="options-view-expiration"
          class="sc3-select-menu sc3-select-menu-inline"
          v-model="expirationDateStringSelection"
        >
          <option
            disabled
            :value="null"
          >-- Select an Expiration --</option>
          <option
            v-for="expiration in expirations"
            :key="expiration.dateString"
            :value="expiration.dateString"
          >{{ expiration.dateString }}</option>
        </select>
      </div>
      <div class="options-show-all-columns-and-refresh-container">
        <SCThreeCheckbox
          id="options-mode-show-all-columns-checkbox"
          label-text="Show All Columns"
          v-model:checkbox-value="showAllColumns"
          :is-label-close-to-icon="true"
          :is-square="true"
          :is-sc3-inline-height="true"
        />
        <button type="button" class="sc3-button sc3-button-extra-small sc3-button-grey options-refresh-button" @click="reloadOptions()">
          <i class="fas fa-fw fa-sync-alt"></i>
        </button>
      </div>
      <span v-show="shouldDisplayStrikeRangeSlider" class="strike-range-container">
        <span class="strike-range-label-and-values-container">
          <span class="strike-range-label">Strike Range:</span>
          <span class="strike-range-values">{{ sliderLesserValue }} - {{ sliderGreaterValue }}</span>
        </span>
        <br />
        <span class="strike-range-inputs">
          <span class="input-range-custom-track"></span>
          <input type="range" :min="sliderMin ?? undefined" :max="sliderMax ?? undefined" step="1" v-model="sliderValue1" />
          <input type="range" :min="sliderMin ?? undefined" :max="sliderMax ?? undefined" step="1" v-model="sliderValue2" />
        </span>
      </span>
      <span class="in-the-money-legend">In The Money</span>
    </div>
    <div class="options-view-container">
      <template v-if="optionsModeView === OPTIONS_MODE_STRADDLE_VIEW || optionsModeView === OPTIONS_MODE_LIST_VIEW">
        <div
          v-if="isLoading || isReloading"
          :class="isLoading ? 'loading-spinner-container' : 'reloading-backdrop'"
        >
          <i class="fad fa-fw fa-spinner-third fa-spin"></i>
        </div>
        <div
          v-if="!isLoading && expirations.length === 0"
          class="options-error-message-container"
        >
          <h5 class="options-error-message-heading">Option Quotes Not Available</h5>
          <p class="options-error-message"><span class="options-error-message-bold-text">Whoops!</span> We're unable to load options data for the symbol you requested. That security may not trade options, or the symbol you entered could be missing or invalid. You can load a different symbol into the chart above and try again.</p>
        </div>
      </template>
      <div v-show="optionsModeView === OPTIONS_MODE_STRADDLE_VIEW && !isLoading && expirations.length > 0" class="straddle-view">
        <table class="sc3-table sc3-table-centered">
          <thead>
            <tr>
              <th class="column-group-header" scope="col" rowspan="1" :colspan="showAllColumns ? '13' : '7'">Calls</th>
              <th class="column-group-header" scope="col" rowspan="1" colspan="1"></th>
              <th class="column-group-header" scope="col" rowspan="1" :colspan="showAllColumns ? '13' : '7'">Puts</th>
            </tr>
            <tr>
              <th scope="col" rowspan="1" colspan="1">Bid</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Bid Size</th>
              <th scope="col" rowspan="1" colspan="1">Ask</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Ask Size</th>
              <th scope="col" rowspan="1" colspan="1">Mid</th>
              <th scope="col" rowspan="1" colspan="1">Vol</th>
              <th scope="col" rowspan="1" colspan="1">OI</th>
              <th scope="col" rowspan="1" colspan="1">IV</th>
              <th scope="col" rowspan="1" colspan="1">Delta</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Theta</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Gamma</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Vega</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Rho</th>
              <th scope="col" rowspan="1" colspan="1">Strike</th>
              <th scope="col" rowspan="1" colspan="1">Bid</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Bid Size</th>
              <th scope="col" rowspan="1" colspan="1">Ask</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Ask Size</th>
              <th scope="col" rowspan="1" colspan="1">Mid</th>
              <th scope="col" rowspan="1" colspan="1">Vol</th>
              <th scope="col" rowspan="1" colspan="1">OI</th>
              <th scope="col" rowspan="1" colspan="1">IV</th>
              <th scope="col" rowspan="1" colspan="1">Delta</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Theta</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Gamma</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Vega</th>
              <th scope="col" rowspan="1" colspan="1" v-if="showAllColumns">Rho</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="callAndPut in filteredCallsAndPuts"
              :key="callAndPut.id"
            >
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }">{{ callAndPut.callBidPrice }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.callBidSize }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }">{{ callAndPut.callAskPrice }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.callAskSize }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }">{{ callAndPut.callMidPrice }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }">{{ callAndPut.callVolume }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }">{{ callAndPut.callOpenInterest }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }">{{ callAndPut.callImpliedVolatility }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }">{{ callAndPut.callDelta }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.callTheta }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.callGamma }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.callVega }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': callAndPut.callInTheMoney, 'white-background-color': !callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.callRho }}</td>
              <td class="grey-background-color">{{ callAndPut.strike }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }">{{ callAndPut.putBidPrice }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.putBidSize }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }">{{ callAndPut.putAskPrice }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.putAskSize }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }">{{ callAndPut.putMidPrice }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }">{{ callAndPut.putVolume }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }">{{ callAndPut.putOpenInterest }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }">{{ callAndPut.putImpliedVolatility }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }">{{ callAndPut.putDelta }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.putTheta }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.putGamma }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.putVega }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': !callAndPut.callInTheMoney, 'white-background-color': callAndPut.callInTheMoney }" v-if="showAllColumns">{{ callAndPut.putRho }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-show="optionsModeView === OPTIONS_MODE_LIST_VIEW && !isLoading && expirations.length > 0" class="list-view">
        <h2 class="list-view-table-title">
          <i class="fas fa-fw fa-arrow-circle-up"></i>
          &nbsp;Calls
        </h2>
        <table class="sc3-table sc3-table-centered">
          <thead>
            <tr>
              <th>Contract</th>
              <th>Last Date</th>
              <th>Strike</th>
              <th>Bid</th>
              <th v-if="showAllColumns">Bid Size</th>
              <th>Ask</th>
              <th v-if="showAllColumns">Ask Size</th>
              <th>Mid</th>
              <th>Vol</th>
              <th>OI</th>
              <th>IV</th>
              <th>Delta</th>
              <th v-if="showAllColumns">Theta</th>
              <th v-if="showAllColumns">Gamma</th>
              <th v-if="showAllColumns">Vega</th>
              <th v-if="showAllColumns">Rho</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="call in filteredCalls"
              :key="call.id"
            >
              <td :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }">{{ call.contract }}</td>
              <td class="small-light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }">{{ call.lastTradeDate }}</td>
              <td class="grey-background-color">{{ call.strike }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }">{{ call.bid }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }" v-if="showAllColumns">{{ call.bidSize }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }">{{ call.ask }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }" v-if="showAllColumns">{{ call.askSize }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }">{{ call.mid }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }">{{ call.volume }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }">{{ call.openInterest }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }">{{ call.impliedVolatility }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }">{{ call.delta }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }" v-if="showAllColumns">{{ call.theta }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }" v-if="showAllColumns">{{ call.gamma }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }" v-if="showAllColumns">{{ call.vega }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': call.inTheMoney, 'white-background-color': !call.inTheMoney }" v-if="showAllColumns">{{ call.rho }}</td>
            </tr>
          </tbody>
        </table>
        <h2 class="list-view-table-title">
          <i class="fas fa-fw fa-arrow-circle-down"></i>
          &nbsp;Puts
        </h2>
        <table class="sc3-table sc3-table-centered">
          <thead>
            <tr>
              <th>Contract</th>
              <th>Last Date</th>
              <th>Strike</th>
              <th>Bid</th>
              <th v-if="showAllColumns">Bid Size</th>
              <th>Ask</th>
              <th v-if="showAllColumns">Ask Size</th>
              <th>Mid</th>
              <th>Vol</th>
              <th>OI</th>
              <th>IV</th>
              <th>Delta</th>
              <th v-if="showAllColumns">Theta</th>
              <th v-if="showAllColumns">Gamma</th>
              <th v-if="showAllColumns">Vega</th>
              <th v-if="showAllColumns">Rho</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="put in filteredPuts"
              :key="put.id"
            >
              <td :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }">{{ put.contract }}</td>
              <td class="small-light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }">{{ put.lastTradeDate }}</td>
              <td class="grey-background-color">{{ put.strike }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }">{{ put.bid }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }" v-if="showAllColumns">{{ put.bidSize }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }">{{ put.ask }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }" v-if="showAllColumns">{{ put.askSize }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }">{{ put.mid }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }">{{ put.volume }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }">{{ put.openInterest }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }">{{ put.impliedVolatility }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }">{{ put.delta }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }" v-if="showAllColumns">{{ put.theta }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }" v-if="showAllColumns">{{ put.gamma }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }" v-if="showAllColumns">{{ put.vega }}</td>
              <td class="light-font" :class="{ 'light-green-background-color': put.inTheMoney, 'white-background-color': !put.inTheMoney }" v-if="showAllColumns">{{ put.rho }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-show="optionsModeView === OPTIONS_MODE_OPTIONS_PLAY_VIEW" class="options-play-view" :class="{ 'options-play-view-left-aligned': isChartLeftAligned }">
        <div v-if="!isLoggedIn" class="options-play-free-view">
          <div class="options-play-free-view-background"></div>
          <p>
            Subscribe to StockCharts.com for access to advanced options analysis tools like this from OptionsPlay - part of our <a href="/optionsplay" class="sc3-button-link sc3-button-link-inline sc3-button-link-light-blue">OptionsPlay Add-On for StockCharts</a>.
            <a href="/optionsplay" class="sc3-button-link sc3-button-link-inline sc3-button-link-light-blue" title="OptionsPlay Add-On for StockCharts">Click HERE</a> to learn more!
          </p>
          <img src="//d.stockcharts.com/img/optionsplay-preview.png" alt="OptionsPlay Add-On" />
        </div>
        <template v-else>
          <div class="options-play-view-heading-container">
            <h3 class="symbol-heading">{{ optionsPlayWidgetSymbol }}<template v-if="optionsPlayWidgetCompanyName"> - {{ optionsPlayWidgetCompanyName }}</template></h3>
            <a class="sc3-button-link" href="//help.stockcharts.com/charts-and-tools/research-tools/options-summary/optionsplay-explorer" target="_blank" rel="noopener noreferrer">
              <i class="far fa-fw fa-info-circle"></i>
              More Info
            </a>
          </div>
          <p v-if="!hasOptionsPlayAddOn" class="options-play-view-purchase-add-on-message">
            This is a DEMO version of the OptionsPlay Explorer, one of the features included with the <a href="/marketplace/addons/optionsplay.html" class="sc3-button-link sc3-button-link-inline sc3-button-link-light-blue">OptionsPlay Add-On for StockCharts</a>.
            This DEMO version can only analyze {{ optionsPlayWidgetSymbol }}.
            Once you install the Add-On, you will be able to analyze any optionable stock in our database.
            <a href="/marketplace/addons/optionsplay.html" class="sc3-button-link sc3-button-link-inline sc3-button-link-light-blue" title="OptionsPlay Add-On for StockCharts">Click HERE</a> to learn more.
          </p>
          <template v-if="!hasSuccessfullyInitializedOptionsPlayWidget">
            <div v-if="isAttemptingToInitializeOptionsPlayWidget || !hasFailedToInitializeOptionsPlayWidget" class="loading-spinner-container"><i class="fad fa-fw fa-spinner-third fa-spin"></i></div>
            <div v-else class="options-play-view-retry-ui">
              We're sorry, something went wrong. Please try again.
              <br />
              <br />
              <button
                type="button"
                class="sc3-button sc3-button-blue"
                @click="setOptionsPlayWidgetSymbol(formattedSymbol)"
              >Try Again</button>
            </div>
          </template>
          <div :id="OPTIONS_PLAY_CONTAINER_ID"></div>
        </template>
      </div>
    </div>
    <div class="back-to-top-button-container" v-if="(optionsModeView === OPTIONS_MODE_STRADDLE_VIEW || optionsModeView === OPTIONS_MODE_LIST_VIEW) && !isLoading && expirations.length > 0">
      <button
        type="button"
        class="sc3-button sc3-button-transparent-and-grey"
        @click="scrollOptionsUIBackToTop()"
      >Back To Top</button>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, watch, nextTick, onMounted } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useUniqueIds } from '@/composables/useUniqueIds';
  import { useOptionsPlay } from '@/composables/useOptionsPlay';
  import { useChartDimensions } from '@/composables/useChartDimensions';
  import { useModes } from '@/composables/useModes';
  import { fetchOptionsData, OptionsData, CallPut } from '@/composables/fetchOptionsData';
  import SCThreeCheckbox from '@/components/checkboxes/SCThreeCheckbox.vue';
  import { isValidNumber } from '@/utils/isValidNumber';
  import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';
  import { OPTIONS_PLAY_CONTAINER_ID } from '@/enums/html-ids';

  const chartStore = useChartStore();
  const { symbol } = storeToRefs(chartStore);
  const { hasOptionsPlayAddOn, isLoggedIn } = useAuthentication();
  const { getPositiveUniqueId } = useUniqueIds();
  const {
    hasSuccessfullyInitializedOptionsPlayWidget,
    hasFailedToInitializeOptionsPlayWidget,
    isAttemptingToInitializeOptionsPlayWidget,
    optionsPlayWidgetSymbol,
    optionsPlayWidgetCompanyName,
    setOptionsPlayWidgetSymbol
  } = useOptionsPlay();
  const { isChartLeftAligned } = useChartDimensions();
  const {
    OPTIONS_MODE,
    currentModeId,
    OPTIONS_MODE_STRADDLE_VIEW,
    OPTIONS_MODE_LIST_VIEW,
    OPTIONS_MODE_OPTIONS_PLAY_VIEW,
    optionsModeView,
    setOptionsModeView
  } = useModes();
  const formattedSymbol = computed(() => {
    return symbol.value
      .replace(/\s/g, '') // remove all whitespace
      .replace(/^[\-_]+/g, '') // remove leading hyphens from inverted symbols and leading underscores from unadjusted symbols
      .toUpperCase();
  });
  const isLoading = ref(false);
  const isReloading = ref(false);
  const rawOptionsData = ref<OptionsData | null>(null);
  const expirationDateStringSelection = ref<string | null>(null);
  let lastFormattedSymbol = '';
  let wasLastOptionsUIViewOptionsPlayView = optionsModeView.value === OPTIONS_MODE_OPTIONS_PLAY_VIEW;
  const showAllColumns = ref(true);
  const expirations = computed(() => {
    if (rawOptionsData.value === null) {
      return [];
    }

    const newExpirations = [];
    for (const expiration of rawOptionsData.value.Expirations) {
      if (!expiration) {
        continue;
      }

      let dateString = `${getDateString(expiration.ExpirationDate)} (${expiration.DaysToExpire})`;

      if (expiration.OptionCycle) {
        dateString += ` (${expiration.OptionCycle})`;
      }

      newExpirations.push({
        dateString,
        calls: createCallsArrayOrPutsArray(expiration.Calls),
        puts: createCallsArrayOrPutsArray(expiration.Puts),
        callsAndPuts: createCallsAndPutsArray(expiration.Calls, expiration.Puts)
      });
    }

    return newExpirations;
  });
  const lastEarningsDate = computed(() => typeof rawOptionsData.value?.LastEarningsDate === 'string' && !isLoading.value ? rawOptionsData.value.LastEarningsDate.trim() : '');
  const nextEarningsDate = computed(() => typeof rawOptionsData.value?.NextEarningsDate === 'string' && !isLoading.value ? rawOptionsData.value.NextEarningsDate.trim() : '');
  const expirationBeingViewed = computed(() => {
    return expirations.value.find(expiration => expiration.dateString === expirationDateStringSelection.value);
  });
  const callsAndPuts = computed(() => {
    return expirationBeingViewed.value?.callsAndPuts ?? [];
  });
  const filteredCallsAndPuts = computed(() => {
    return callsAndPuts.value.filter(callAndPut => {
      if (!isValidNumber(callAndPut.strike)) {
        return true;
      }

      if (sliderLesserValue.value !== null && callAndPut.strike < sliderLesserValue.value) {
        return false;
      }

      if (sliderGreaterValue.value !== null && callAndPut.strike > sliderGreaterValue.value) {
        return false;
      }

      return true;
    });
  });
  const calls = computed(() => {
    return expirationBeingViewed.value?.calls ?? [];
  });
  const filteredCalls = computed(() => {
    return calls.value.filter(call => {
      if (!isValidNumber(call.strike)) {
        return true;
      }

      if (sliderLesserValue.value !== null && call.strike < sliderLesserValue.value) {
        return false;
      }

      if (sliderGreaterValue.value !== null && call.strike > sliderGreaterValue.value) {
        return false;
      }

      return true;
    });
  });
  const puts = computed(() => {
    return expirationBeingViewed.value?.puts ?? [];
  });
  const filteredPuts = computed(() => {
    return puts.value.filter(put => {
      if (!isValidNumber(put.strike)) {
        return true;
      }

      if (sliderLesserValue.value !== null && put.strike < sliderLesserValue.value) {
        return false;
      }

      if (sliderGreaterValue.value !== null && put.strike > sliderGreaterValue.value) {
        return false;
      }

      return true;
    });
  });
  const optionsUiContainerElement = ref<HTMLElement>();
  const sliderMin = ref<number | null>(null);
  const sliderMax = ref<number | null>(null);
  const sliderValue1 = ref<number | null>(null);
  const sliderValue2 = ref<number | null>(null);
  const sliderLesserValue = computed(() => {
    if (sliderValue1.value === null && sliderValue2.value === null) {
      return null;
    }

    if (sliderValue2.value === null) {
      return sliderValue1.value;
    }

    if (sliderValue1.value === null) {
      return sliderValue2.value;
    }

    return Math.min(sliderValue1.value, sliderValue2.value);
  });
  const sliderGreaterValue = computed(() => {
    if (sliderValue1.value === null && sliderValue2.value === null) {
      return null;
    }

    if (sliderValue2.value === null) {
      return sliderValue1.value;
    }

    if (sliderValue1.value === null) {
      return sliderValue2.value;
    }

    return Math.max(sliderValue1.value, sliderValue2.value);
  });
  const shouldDisplayStrikeRangeSlider = computed(() => {
    return (
      sliderMin.value !== null &&
      sliderMax.value !== null &&
      sliderLesserValue.value !== null &&
      sliderGreaterValue.value !== null
    );
  });
  const strikeRange = computed(() => {
    const callStrikes = calls.value.map(obj => obj.strike);
    const putStrikes = puts.value.map(obj => obj.strike);
    const callPutStrikes = [...callStrikes, ...putStrikes];
    const strikeMinMax = callPutStrikes.length ? [Math.min(...callPutStrikes), Math.max(...callPutStrikes)] as const : [null, null] as const;
    return strikeMinMax;
  });

  async function resetSliderRange(shouldCompletelyResetRange: boolean) {
    const [lowerBound, upperBound] = strikeRange.value;

    if (lowerBound === null || upperBound === null) {
      sliderMin.value = null;
      sliderMax.value = null;
      await setSliderValues(null, null);
      return;
    }

    const previousSliderMin = sliderMin.value;
    const previousSliderMax = sliderMax.value;
    const newSliderMin = Math.floor(lowerBound);
    const newSliderMax = Math.ceil(upperBound);
    sliderMin.value = newSliderMin;
    sliderMax.value = newSliderMax;

    if (
      shouldCompletelyResetRange ||
      previousSliderMin === null ||
      previousSliderMax === null ||
      sliderLesserValue.value === null ||
      sliderGreaterValue.value === null
    ) {
      await setSliderValues(newSliderMin, newSliderMax);
      return;
    }

    let newSliderValue1 = sliderLesserValue.value;
    let newSliderValue2 = sliderGreaterValue.value;

    if (newSliderValue1 === previousSliderMin) {
      newSliderValue1 = newSliderMin;
    }

    if (newSliderValue2 === previousSliderMax) {
      newSliderValue2 = newSliderMax;
    }

    if (newSliderValue1 > newSliderMax || newSliderValue2 < newSliderMin) {
      await setSliderValues(newSliderMin, newSliderMax);
      return;
    }

    if (newSliderValue1 < newSliderMin) {
      newSliderValue1 = newSliderMin;
    }

    if (newSliderValue2 > newSliderMax) {
      newSliderValue2 = newSliderMax;
    }

    await setSliderValues(newSliderValue1, newSliderValue2);
  }

  async function setSliderValues(newSliderValue1: number | null, newSliderValue2: number | null) {
    await nextTick();
    await waitForDomToRepaint();

    sliderValue1.value = newSliderValue1;
    sliderValue2.value = newSliderValue2;
  }

  function getNumberWithCommas(number: number) {
    if (!number) {
      return '0';
    }

    const parts = number.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=([0-9]{3})+(?![0-9]))/g, ',');

    return parts.join('.');
  }

  async function resetOptions() {
    expirationDateStringSelection.value = null;
    rawOptionsData.value = null;

    await resetSliderRange(true);
  }

  function getEasternTime(dateObj: Date) {
    const dateString = dateObj.toLocaleString('en-US', {
      timeZone: 'America/New_York'
    });

    return dateString.split(', ')[1];
  }

  function getDateFromEntry(entry: CallPut) {
    if (!entry.Date || !entry.Time) {
      return '-';
    }

    const date = new Date(`${entry.Date} ${entry.Time}`);
    const year = date.getFullYear();
    const month = toFixedDigitNumberString(date.getMonth() + 1);
    const day = toFixedDigitNumberString(date.getDate());
    const time = getEasternTime(date);

    return `${year}-${month}-${day} ${time} EDT`;
  }

  function createCallsArrayOrPutsArray(callsOrPuts: CallPut[]) {
    if (!Array.isArray(callsOrPuts)) {
      return [];
    }

    const filteredDataObjects = [];

    for (const entry of callsOrPuts) {
      if (
        !entry ||
        typeof entry.Symbol !== 'string' ||
        /^[0-9]/.test(entry.Symbol)
      ) {
        continue;
      }

      filteredDataObjects.push({
        id: getPositiveUniqueId(),
        contract: entry.Symbol,
        lastTradeDate: getDateFromEntry(entry),
        strike: entry.StrikePrice,
        bid: entry.Bid.toFixed(2),
        ask: entry.Ask.toFixed(2),
        mid: entry.Mid.toFixed(2),
        askSize: getNumberWithCommas(entry.AskSize),
        bidSize: getNumberWithCommas(entry.BidSize),
        volume: getNumberWithCommas(entry.Volume),
        openInterest: getNumberWithCommas(entry.OpenInterest),
        gamma: entry.Gamma.toFixed(2),
        delta: entry.Delta.toFixed(2),
        theta: entry.Theta.toFixed(2),
        rho: entry.Rho.toFixed(2),
        vega: entry.Vega.toFixed(2),
        impliedVolatility: entry.ImpVol.toFixed(2) + '%',
        inTheMoney: entry.InTheMoney
      });
    }

    return filteredDataObjects;
  }

  function replaceIndicator(idString: string, replaceCharacter: string) {
    const indicatorIndex = Math.max(idString.lastIndexOf('C'), idString.lastIndexOf('P'));

    return replaceAt(idString, indicatorIndex, replaceCharacter);
  }

  interface FormattedCallPut {
    id: number;
    callBidPrice: string;
    callBidSize: string;
    callAskPrice: string;
    callAskSize: string;
    callMidPrice: string;
    callVolume: string;
    callOpenInterest: string;
    callImpliedVolatility: string;
    callGamma: string;
    callDelta: string;
    callTheta: string;
    callRho: string;
    callVega: string;
    strike: number;
    putBidPrice: string;
    putBidSize: string;
    putAskPrice: string;
    putAskSize: string;
    putMidPrice: string;
    putVolume: string;
    putOpenInterest: string;
    putImpliedVolatility: string;
    putGamma: string;
    putDelta: string;
    putTheta: string;
    putRho: string;
    putVega: string;
    callInTheMoney: boolean;
  }

  function createCallsAndPutsArray(callsArray: CallPut[], putsArray: CallPut[]) {
    if (
      !Array.isArray(callsArray) ||
      !Array.isArray(putsArray)
    ) {
      return [];
    }

    const callPutRows = new Map<string, FormattedCallPut>();

    for (const call of callsArray) {
      const key = replaceIndicator(call.Symbol, 'X');

      callPutRows.set(key, {
        id: getPositiveUniqueId(),
        callBidPrice: call.Bid.toFixed(2),
        callAskPrice: call.Ask.toFixed(2),
        callMidPrice: call.Mid.toFixed(2),
        callAskSize: getNumberWithCommas(call.AskSize),
        callBidSize: getNumberWithCommas(call.BidSize),
        callVolume: getNumberWithCommas(call.Volume),
        callOpenInterest: getNumberWithCommas(call.OpenInterest),
        callImpliedVolatility: call.ImpVol === 0 ? '0.00' : call.ImpVol.toFixed(2) + '%',
        callGamma: call.Gamma.toFixed(2),
        callDelta: call.Delta.toFixed(2),
        callTheta: call.Theta.toFixed(2),
        callRho: call.Rho.toFixed(2),
        callVega: call.Vega.toFixed(2),
        strike: call.StrikePrice,
        putBidPrice: '0',
        putAskPrice: '0',
        putMidPrice: '0',
        putAskSize: '0',
        putBidSize: '0',
        putVolume: '0',
        putOpenInterest: '0',
        putImpliedVolatility: '0.00',
        putGamma: '0.00',
        putDelta: '0.00',
        putTheta: '0.00',
        putRho: '0.00',
        putVega: '0.00',
        callInTheMoney: call.InTheMoney
      });
    }

    for (const put of putsArray) {
      const key = replaceIndicator(put.Symbol, 'X');

      if (callPutRows.has(key)) {
        const callObject = callPutRows.get(key)!;

        callObject.putBidPrice = put.Bid.toFixed(2);
        callObject.putBidSize = getNumberWithCommas(put.BidSize);
        callObject.putAskPrice = put.Ask.toFixed(2);
        callObject.putAskSize = getNumberWithCommas(put.AskSize);
        callObject.putMidPrice = put.Mid.toFixed(2);
        callObject.putVolume = getNumberWithCommas(put.Volume);
        callObject.putOpenInterest = getNumberWithCommas(put.OpenInterest);
        callObject.putImpliedVolatility = put.ImpVol === 0 ? '0.00' : put.ImpVol.toFixed(2) + '%';
        callObject.putGamma = put.Gamma.toFixed(2);
        callObject.putDelta = put.Delta.toFixed(2);
        callObject.putTheta = put.Theta.toFixed(2);
        callObject.putRho = put.Rho.toFixed(2);
        callObject.putVega = put.Vega.toFixed(2);
      } else {
        callPutRows.set(key, {
          id: getPositiveUniqueId(),
          callBidPrice: '0',
          callBidSize: '0',
          callAskPrice: '0',
          callAskSize: '0',
          callMidPrice: '0',
          callVolume: '0',
          callOpenInterest: '0',
          callImpliedVolatility: '0.00',
          callGamma: '0.00',
          callDelta: '0.00',
          callTheta: '0.00',
          callRho: '0.00',
          callVega: '0.00',
          strike: put.StrikePrice,
          putBidPrice: put.Bid.toFixed(2),
          putBidSize: getNumberWithCommas(put.BidSize),
          putAskPrice: put.Ask.toFixed(2),
          putAskSize: getNumberWithCommas(put.AskSize),
          putMidPrice: put.Mid.toFixed(2),
          putVolume: getNumberWithCommas(put.Volume),
          putOpenInterest: getNumberWithCommas(put.OpenInterest),
          putImpliedVolatility: put.ImpVol === 0 ? '0.00' : put.ImpVol.toFixed(2) + '%',
          putGamma: put.Gamma.toFixed(2),
          putDelta: put.Delta.toFixed(2),
          putTheta: put.Theta.toFixed(2),
          putRho: put.Rho.toFixed(2),
          putVega: put.Vega.toFixed(2),
          callInTheMoney: !put.InTheMoney
        });
      }
    }

    return Array.from(callPutRows.values());
  }

  function getDateString(date: string) {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'long',
      day: 'numeric',
      year: 'numeric',
      month: 'long',
    });
  }

  function toFixedDigitNumberString(num: number) {
    return String(num).padStart(2, '0');
  }

  function replaceAt(string: string, index: number, replacement: string) {
    if (index < 0 || index > string.length - 1) {
      return string;
    }

    return string.substring(0, index) + replacement + string.substring(index + replacement.length);
  }

  function scrollOptionsUIBackToTop() {
    const top = optionsUiContainerElement.value?.getBoundingClientRect().top ?? -window.scrollY;

    window.scrollBy({
      top,
      behavior: 'smooth'
    });
  }

  async function reloadOptions() {
    if (isLoading.value || isReloading.value || (optionsModeView.value !== OPTIONS_MODE_STRADDLE_VIEW && optionsModeView.value !== OPTIONS_MODE_LIST_VIEW)) {
      return;
    }

    const currentSymbol = formattedSymbol.value;
    isReloading.value = true;

    const rawOptionsDataResponse = await fetchOptionsData(formattedSymbol.value);

    if (currentSymbol !== formattedSymbol.value) {
      return;
    }

    rawOptionsData.value = rawOptionsDataResponse;
    isReloading.value = false;
  }

  async function loadOptionsForSymbol(currentSymbol: string) {
    if (rawOptionsData.value?.symbol === currentSymbol) {
      return;
    }

    isLoading.value = true;
    isReloading.value = false;

    try {
      await resetOptions();

      if (currentSymbol !== formattedSymbol.value) {
        return;
      }

      const rawOptionsDataResponse = await fetchOptionsData(currentSymbol);

      if (currentSymbol !== formattedSymbol.value) {
        return;
      }

      rawOptionsData.value = rawOptionsDataResponse;
      expirationDateStringSelection.value = expirations.value[0] ? expirations.value[0].dateString : null;
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Error occurred while loading options for symbol: ${currentSymbol}.`, e);

      if (currentSymbol === formattedSymbol.value) {
        await resetOptions();
      }
    }

    if (currentSymbol === formattedSymbol.value) {
      isLoading.value = false;
    }
  }

  async function handleCurrentModeIdOrFormattedSymbolOrOptionsUIViewChanged() {
    if (currentModeId.value !== OPTIONS_MODE.id) {
      return;
    }

    const hasFormattedSymbolChanged = lastFormattedSymbol !== formattedSymbol.value;
    lastFormattedSymbol = formattedSymbol.value;
    const isCurrentOptionsUIViewOptionsPlayView = optionsModeView.value === OPTIONS_MODE_OPTIONS_PLAY_VIEW;
    const hasWasLastOptionsUIViewOptionsPlayViewChanged = wasLastOptionsUIViewOptionsPlayView !== isCurrentOptionsUIViewOptionsPlayView;
    wasLastOptionsUIViewOptionsPlayView = isCurrentOptionsUIViewOptionsPlayView;

    if (!hasFormattedSymbolChanged && !hasWasLastOptionsUIViewOptionsPlayViewChanged) {
      return;
    }

    if (isCurrentOptionsUIViewOptionsPlayView) {
      await setOptionsPlayWidgetSymbol(formattedSymbol.value);
      return;
    }

    await loadOptionsForSymbol(formattedSymbol.value);
  }

  async function handleExpirationDateStringChanged() {
    await resetSliderRange(false);
  }

  async function handleExpirationsChanged(newExpirations: typeof expirations.value) {
    const stillHasExpirationDateStringSelection = !!expirations.value.find(expiration => expiration.dateString === expirationDateStringSelection.value);
    if (!stillHasExpirationDateStringSelection) {
      expirationDateStringSelection.value = newExpirations[0] ? newExpirations[0].dateString : null;
    }
  }

  onMounted(() => {
    watch(currentModeId, handleCurrentModeIdOrFormattedSymbolOrOptionsUIViewChanged);
    watch(formattedSymbol, handleCurrentModeIdOrFormattedSymbolOrOptionsUIViewChanged);
    watch(optionsModeView, handleCurrentModeIdOrFormattedSymbolOrOptionsUIViewChanged);
    watch(expirationDateStringSelection, handleExpirationDateStringChanged);
    watch(expirations, handleExpirationsChanged);

    handleCurrentModeIdOrFormattedSymbolOrOptionsUIViewChanged();
  });
</script>

<style scoped>
  .options-ui-container {
    margin: 0px;
  }

  .loading-spinner-container {
    text-align: center;
    font-size: 36px;
    padding: 12px;
  }

  .options-error-message-container {
    text-align: center;
  }

  .options-error-message-heading {
    font-weight: bold;
    font-size: 18px;
    margin: 18px 0px 4px 0px;
  }

  .options-error-message {
    display: inline-block;
    font-size: 16px;
    text-align: center;
    margin: 0px 0px 10px 0px;
    padding: 10px;
    color: #5a6672;
    max-width: 620px;
  }

  .options-error-message-bold-text {
    font-weight: bold;
  }

  .options-play-view {
    text-align: center;
  }

  .options-play-view-left-aligned {
    text-align: left;
  }

  .options-play-free-view {
    position: relative;
    z-index: 1;
    background-color: #243342;
    max-width: 900px;
    text-align: center;
    padding: 30px 0px;
  }

  .options-play-free-view .options-play-free-view-background {
    position: absolute;
    z-index: -1;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: url(//d.stockcharts.com/img/bg-chart-graphic-01.jpg) no-repeat center center scroll;
    background-size: cover;
    opacity: 0.15;
    width: 100%;
    height: 100%;
  }

  .options-play-free-view p {
    display: inline-block;
    max-width: 530px;
    margin: 0px 0px 30px 0px;
    line-height: 1.45;
    font-size: 16px;
    color: #e2e5e9;
  }

  .options-play-free-view img {
    max-width: calc(100% - 24px);
  }

  .options-play-view-retry-ui {
    font-size: 16px;
    font-weight: bold;
    padding: 10px;
    text-align: center;
  }

  .options-ui-header {
    display: flex;
    gap: 84px;
    align-items: center;
    font-size: 14px;
  }

  .options-play-view-heading-container {
    display: inline-flex;
    justify-content: space-between;
    width: 100%;
    text-align: left;
  }

  .options-ui-header,
  .options-play-view-heading-container {
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 700;
    padding: 6px 6px 0px 6px;
  }

  .options-play-view-purchase-add-on-message {
    padding: 2px 6px;
  }

  .options-play-view-purchase-add-on-message,
  .options-play-view-purchase-add-on-message a {
    font-size: 16px;
  }

  @media (max-width: 767px) {
    .options-ui-header {
      flex-direction: column;
      gap: 3px;
    }
  }

  .symbol-heading {
    display: inline-block;
    vertical-align: top;
    font-size: 18px;
    margin: 0px;
  }

  .options-show-all-columns-and-refresh-container label {
    width: fit-content;
    white-space: nowrap;
  }

  .options-ui-view-buttons {
    display: flex;
    align-items: center;
    justify-content: left;
    gap: 5px;
    background-color: #d0dbe2;
    height: 50px;
    overflow: visible;
    padding: 0px 6px;
  }

  .options-ui-view-button {
    height: 30px;
    padding: 0px 12px;
    font-size: 14px;
    line-height: 0px;
    border: 2px solid transparent;
    border-radius: 3px;
    font-weight: 700;
    color: #3281d0;
    background-color: transparent;
    transition: all .35s ease;
  }

  .options-ui-view-button:hover,
  .options-ui-view-button:active,
  .options-ui-view-button:focus {
    border-color: #c0ced8;
  }

  .options-ui-view-button-active,
  .options-ui-view-button-active:hover,
  .options-ui-view-button-active:active,
  .options-ui-view-button-active:focus {
    border-color: #3281d0;
  }

  .options-ui-view-button img {
    height: 17px;
  }

  .strike-range-container {
    display: inline-block;
    padding: 6px 6px 0px 6px;
    position: relative;
  }

  .strike-range-label-and-values-container {
    display: inline-block;
  }

  .strike-range-label {
    font-weight: 400;
    color: #707f8f;
  }

  .strike-range-values {
    font-weight: 700;
    color: #2d3339;
    margin: 0px 0px 0px 4px;
  }

  .strike-range-inputs {
    display: inline-block;
    position: relative;
    width: 320px;
    margin: 12px 0px 0px 0px;
  }

  input[type=range] {
    width: 100%;
    position: absolute;
    left: 0px;
    top: 4px;
    height: 0px;
    margin: 0px;
  }

  input[type=range]::-webkit-slider-thumb {
    top: -8px;
  }

  input[type=range]::-webkit-slider-runnable-track {
    height: 0px;
  }

  input[type=range]::-moz-range-track {
    height: 0px;
  }

  input[type=range]::-ms-track {
    height: 0px;
  }

  .options-view-container {
    position: relative;
  }

  .reloading-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    z-index: 1000;
  }

  .reloading-backdrop i {
    font-size: 70px;
    top: 150px;
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .options-refresh-button {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 27px;
    width: 27px;
  }

  .input-range-custom-track {
    display: inline-block;
    vertical-align: top;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background-color: #c6ccd2;
  }

  .table-ui-container {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(2, fit-content(100%)) 1fr repeat(2, fit-content(100%));
    justify-content: space-between;
    align-items: center;
    padding: 6px;
  }

  .options-select-container {
    grid-row: 1;
    grid-column: 1;
    margin: auto;
  }

  .strike-range-container {
    grid-row: 1;
    grid-column: 4;
  }

  .options-show-all-columns-and-refresh-container {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 18px;
    grid-row: 1;
    grid-column: 2;
  }

  .in-the-money-legend {
    margin-top: 23px;
    grid-row: 1;
    grid-column: 5;
    display: inline-block;
    margin-left: auto;
    width: fit-content;
    border: 1px solid #c0ced8;
    padding: 5px;
    background-color: #eafaf2;
    font-size: 11px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.5);
  }

  #options-play-container { /* "options-play-container" matches the value of the OPTIONS_PLAY_CONTAINER_ID html-id enum */
    text-align: center;
    max-width: 100%;
    overflow-x: auto;
  }

  .options-play-view-left-aligned #options-play-container { /* "options-play-container" matches the value of the OPTIONS_PLAY_CONTAINER_ID html-id enum */
    text-align: left;
  }

  @media (max-width: 767px) {
    .table-ui-container {
      grid-template-columns: 1fr 1fr;
    }

    .options-select-container {
      grid-row: 1 !important;
      grid-column: 1 / -1 !important;
      margin: auto;
    }

    .strike-range-container {
      grid-row: 2 !important;
      grid-column: 1 / -1 !important;
    }

    .strike-range-inputs {
      width: 100%;
    }

    .in-the-money-legend {
      margin-top: 0px;
      margin-right: 6px;
      grid-row: 3 !important;
      grid-column: 2 !important;
    }

    .options-show-all-columns-and-refresh-container {
      margin-top: 0px;
      margin-left: 6px;
      grid-row: 3 !important;
      grid-column: 1 !important;
    }
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    .table-ui-container {
      grid-template-columns: auto auto;
    }

    .options-select-container {
      grid-row: 1 !important;
      grid-column: 1 !important;
    }

    .options-show-all-columns-and-refresh-container {
      margin-top: 0px;
      width: fit-content;
      grid-row: 2 !important;
      grid-column: 1 !important;
    }

    .strike-range-container {
      grid-row: 1 !important;
      grid-column: 2 !important;
    }

    .in-the-money-legend {
      margin-top: 0px;
      grid-row: 2 !important;
      grid-column: 2 !important;
    }
  }

  .straddle-view,
  .list-view {
    position: relative;
    padding: 6px;
    overflow: auto;
  }

  .list-view {
    padding-top: 0px;
  }

  .list-view-table-title {
    margin: 12px 0px 5px 0px;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 30px;
    font-weight: 700;
    color: #707f8f;
  }

  .back-to-top-button-container {
    text-align: center;
    padding: 0px 6px 6px 6px;
  }










  /* TODO: remove this once it gets replaced with <DataTable /> */

  table.sc3-table {
    border: 1px solid #c0ced8;
    margin: 0px;
    background-color: transparent;
    max-width: 100%;
    width: 100%;
    border-collapse: collapse;
  }

  table.sc3-table thead {
    background-color: #707f8f;
    margin: 0px;
    background-color: transparent;
    max-width: 100%;
    width: 100%;
  }

  table.sc3-table thead tr th {
    background-color: #707f8f;
    border: 1px solid #c0ced8;
    border-top: none;
    vertical-align: bottom;
    padding: 8px;
    white-space: nowrap;
    text-transform: uppercase;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 900;
    text-align: left;
    color: white;
  }

  table.sc3-table thead tr th.column-group-header {
    padding: 10px;
    background-color: transparent;
    border: none;
    font-size: 20px;
    color: #707f8f;
    text-align: center;
    text-transform: none;
  }

  table.sc3-table tbody tr td {
    background-color: white;
    color: #2d3339;
    font-size: 14px;
    font-weight: 700;
    border: 1px solid #c0ced8;
    vertical-align: middle;
    padding: 5px;
  }

  table.sc3-table.sc3-table-centered thead tr th,
  table.sc3-table.sc3-table-centered tbody tr td {
    text-align: center;
  }

  table.sc3-table tbody tr:nth-of-type(odd) td {
    background-color: #f2f5f7;
  }

  table.sc3-table tbody tr:hover td {
    background-color: #dfe7ec;
  }

  table.sc3-table tbody tr td.bold-font {
    font-weight: 900;
  }

  table.sc3-table tbody tr td.light-font {
    font-weight: 400;
  }

  table.sc3-table tbody tr td.small-light-font {
    font-weight: 400;
    color: #707f8f;
    font-size: 12px;
  }

  table.sc3-table tbody tr td.green-font,
  table.sc3-table tbody tr td span.green-font {
    color: #1e824c;
  }

  table.sc3-table tbody tr td.green-font,
  table.sc3-table tbody tr td span.green-font {
    color: #1e824c;
  }

  table.sc3-table tbody tr td.red-font,
  table.sc3-table tbody tr td span.red-font {
    color: #cc000e;
  }

  table.sc3-table tbody tr td.light-green-background-color,
  table.sc3-table tbody tr td span.light-green-background-color {
    background-color: #eafaf2;
  }

  table.sc3-table tbody tr td.grey-background-color,
  table.sc3-table tbody tr td span.grey-background-color {
    background-color: #d2dbe2;
  }

  table.sc3-table tbody tr td.white-background-color,
  table.sc3-table tbody tr td span.white-background-color {
    background-color: white;
  }


</style>
